import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet } from '../api/client'

/**
 * CAUTAREA GLOBALA - `OdSearch` din `public/js/od_ux.js`, cu acelasi markup
 * (`.od-search`, `.od-search-panel`, `.od-search-hit`, foaia `od_ux.css`).
 *
 * Se deschide cu Ctrl+K (sau Cmd+K), cu `/` cand nu scrii intr-un camp, sau
 * din lupa din antet (`.od-search-open`). Cere `GET /api/v2/general/search`
 * la 180 ms dupa ultima tasta, de la doua litere; sagetile umbla prin
 * rezultate, Enter deschide, Escape inchide. Ce a cautat omul ramane in
 * `localStorage` (zece), pe masina lui - nu pe server.
 *
 * Adresele vin ca in portal (`inventoree/device/12`) si se deschid sub
 * `/app/`, prin router - fara reincarcarea paginii.
 */
export const SEARCH_URL = '/general/search'
const RECENT_KEY = 'odSearchRecent'
const RECENT_MAX = 10
const DEBOUNCE_MS = 180

export interface SearchHit {
  title: string
  note: string
  url: string
  icon: string
}

export interface SearchGroup {
  source: string
  items: SearchHit[]
}

function recent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY)
    const list: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(list) ? list.map(String).slice(0, RECENT_MAX) : []
  } catch {
    return []
  }
}

function remember(term: string): string[] {
  term = term.trim()
  if (term.length < 2) return recent()
  const list = recent().filter((old) => old.toLowerCase() !== term.toLowerCase())
  list.unshift(term)
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)))
  } catch {
    /* fara memorie: o cautare nu pica fiindca nu s-a putut tine minte */
  }
  return list.slice(0, RECENT_MAX)
}

function typing(node: EventTarget | null): boolean {
  const el = node as HTMLElement | null
  if (!el) return false
  const tag = (el.tagName || '').toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable
}

export function SearchPalette() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [hint, setHint] = useState('Type at least two characters.')
  const [groups, setGroups] = useState<SearchGroup[]>([])
  const [on, setOn] = useState(0)
  const [past, setPast] = useState<string[]>([])
  const [failed, setFailed] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const seq = useRef(0)
  const timer = useRef<number | null>(null)

  const hits: Array<SearchHit> = groups.flatMap((g) => g.items)

  const ask = useCallback(async (q: string): Promise<void> => {
    const mine = ++seq.current
    setHint('Searching...')
    setFailed(null)
    try {
      const reply = await apiGet<{ groups: SearchGroup[] }>(`${SEARCH_URL}?q=${encodeURIComponent(q)}`)
      if (mine !== seq.current) return
      setGroups(reply.groups ?? [])
      setOn(0)
      setHint(reply.groups?.length ? '' : `Nothing found for "${q}".`)
      setPast(remember(q))
    } catch {
      if (mine !== seq.current) return
      setGroups([])
      setFailed(q)
      setHint('')
    }
  }, [])

  const schedule = (q: string): void => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    if (q.trim().length < 2) {
      setGroups([])
      setHint('Type at least two characters.')
      return
    }
    timer.current = window.setTimeout(() => void ask(q.trim()), DEBOUNCE_MS)
  }

  const show = useCallback((): void => {
    setPast(recent())
    setTerm('')
    setGroups([])
    setHint('Type at least two characters.')
    setOpen(true)
    window.setTimeout(() => input.current?.focus(), 0)
  }, [])

  const go = (hit: SearchHit): void => {
    remember(term)
    setOpen(false)
    void navigate({ to: '/' + hit.url.replace(/^\/+/, '') })
  }

  useEffect(() => {
    const onKey = (ev: KeyboardEvent): void => {
      if ((ev.ctrlKey || ev.metaKey) && (ev.key === 'k' || ev.key === 'K')) {
        ev.preventDefault()
        if (open) setOpen(false)
        else show()
        return
      }
      if (ev.key === 'Escape' && open) {
        setOpen(false)
        return
      }
      if (ev.key === '/' && !typing(ev.target) && !open) {
        ev.preventDefault()
        show()
      }
    }
    const onClick = (ev: MouseEvent): void => {
      const trigger = (ev.target as HTMLElement | null)?.closest('.od-search-open')
      if (!trigger) return
      ev.preventDefault()
      show()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('click', onClick)
    }
  }, [open, show])

  if (!open) return null

  let index = -1

  return (
    <div
      className="od-search"
      style={{ display: 'flex' }}
      onClick={(ev) => {
        if ((ev.target as HTMLElement).classList.contains('od-search-back') || ev.target === ev.currentTarget) setOpen(false)
      }}
    >
      <div className="od-search-back"></div>
      <div className="od-search-panel bg-module" role="dialog" aria-label="Search" style={{ top: 58, right: 16, left: 'auto' }}>
        <div className="od-search-field">
          <i className="fas fa-search" aria-hidden="true"></i>
          <input
            ref={input}
            type="text"
            className="od-search-input"
            autoComplete="off"
            spellCheck={false}
            placeholder="Machine name, IP, serial, ticket number, or a screen you are looking for"
            value={term}
            onChange={(e) => {
              setTerm(e.target.value)
              schedule(e.target.value)
            }}
            onKeyDown={(ev) => {
              if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
                ev.preventDefault()
                if (hits.length === 0) return
                setOn((i) => (i + (ev.key === 'ArrowDown' ? 1 : -1) + hits.length) % hits.length)
                return
              }
              if (ev.key === 'Enter' && hits[on]) {
                ev.preventDefault()
                go(hits[on])
              }
            }}
          />
          <kbd className="od-search-esc">Esc</kbd>
        </div>
        <div className="od-search-body">
          <div className="od-search-side">
            <div className="od-search-where">Recent</div>
            <div className="od-search-recent">
              {past.length === 0 ? (
                <p className="od-search-none">What you search for shows up here.</p>
              ) : (
                past.map((old) => (
                  <button
                    key={old}
                    type="button"
                    className="od-search-again"
                    data-term={old}
                    onClick={() => {
                      setTerm(old)
                      input.current?.focus()
                      void ask(old)
                    }}
                  >
                    <i className="fas fa-history" aria-hidden="true"></i>
                    <span>{old}</span>
                  </button>
                ))
              )}
            </div>
          </div>
          <div className="od-search-out" role="listbox">
            {failed !== null ? (
              <p className="od-search-hint">
                Could not load the search results.{' '}
                <button type="button" className="od-linklike" onClick={() => void ask(failed)}>
                  Try again
                </button>
              </p>
            ) : hint !== '' ? (
              <p className="od-search-hint">{hint}</p>
            ) : (
              groups.map((group) => (
                <div key={group.source} style={{ display: 'contents' }}>
                  <div className="od-search-group">{group.source}</div>
                  {group.items.map((item) => {
                    index += 1
                    const i = index
                    return (
                      <a
                        key={`${group.source}:${item.url}`}
                        className={`od-search-hit${i === on ? ' is-on' : ''}`}
                        href={'/app/' + item.url.replace(/^\/+/, '')}
                        role="option"
                        aria-selected={i === on}
                        onMouseEnter={() => setOn(i)}
                        onClick={(ev) => {
                          ev.preventDefault()
                          go(item)
                        }}
                      >
                        <i className={`fas ${item.icon || 'fa-file-alt'}`} aria-hidden="true"></i>
                        <span className="od-search-title">{item.title}</span>
                        <span className="od-search-note">{item.note || ''}</span>
                      </a>
                    )
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
